import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StatusFilter, SortColumn, SortDirection } from './types';

export const useOverviewState = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const openGuideId = searchParams.get('open') ?? null;
  const statusFilter = (searchParams.get('filter') as StatusFilter) ?? 'all';
  const searchQuery = searchParams.get('q') ?? '';
  const sortColumn = (searchParams.get('sort') as SortColumn) ?? 'docName';
  const sortDirection = (searchParams.get('dir') as SortDirection) ?? 'asc';
  const ownerFilter = searchParams.get('owner') ?? '';

  const toggleGuide = useCallback(
    (guideId: string) => {
      setSearchParams(
        (prev) => {
          if (prev.get('open') === guideId) {
            prev.delete('open');
          } else {
            prev.set('open', guideId);
          }
          return prev;
        },
        { replace: true, preventScrollReset: true },
      );
    },
    [setSearchParams],
  );

  const setStatusFilter = useCallback(
    (filter: StatusFilter) => {
      setSearchParams(
        (prev) => {
          if (filter === 'all') {
            prev.delete('filter');
          } else {
            prev.set('filter', filter);
          }
          prev.delete('open');
          return prev;
        },
        { replace: true, preventScrollReset: true },
      );
    },
    [setSearchParams],
  );

  const setSearchQuery = useCallback(
    (query: string) => {
      setSearchParams(
        (prev) => {
          if (query === '') {
            prev.delete('q');
          } else {
            prev.set('q', query);
          }
          return prev;
        },
        { replace: true, preventScrollReset: true },
      );
    },
    [setSearchParams],
  );

  const setOwnerFilter = useCallback(
    (owner: string) => {
      setSearchParams(
        (prev) => {
          if (owner === '') {
            prev.delete('owner');
          } else {
            prev.set('owner', owner);
          }
          prev.delete('open');
          return prev;
        },
        { replace: true, preventScrollReset: true },
      );
    },
    [setSearchParams],
  );

  const isOpen = useCallback((guideId: string) => openGuideId === guideId, [openGuideId]);

  const setSort = useCallback(
    (column: SortColumn) => {
      setSearchParams(
        (prev) => {
          const currentColumn = prev.get('sort') ?? 'docName';
          const currentDirection = prev.get('dir') ?? 'asc';

          if (currentColumn === column) {
            // Toggle direction
            prev.set('dir', currentDirection === 'asc' ? 'desc' : 'asc');
          } else {
            // New column, default to ascending
            prev.set('sort', column);
            prev.set('dir', 'asc');
          }
          return prev;
        },
        { replace: true, preventScrollReset: true },
      );
    },
    [setSearchParams],
  );

  return { toggleGuide, isOpen, statusFilter, setStatusFilter, searchQuery, setSearchQuery, sortColumn, sortDirection, setSort, ownerFilter, setOwnerFilter };
};
